// src/routes/dashboard/dashboard-meta.ts
/**
 * Costanti e helper per la dashboard operativa: mapping delle sezioni della pipeline
 * (label, route, icona — icone speculari a quelle della sidebar in AppLayout), override
 * delle etichette di stato dove il significato cambia in base al reparto, e funzioni di
 * formattazione (delta KPI, giorni a scadenza, mese breve).
 */

import React from 'react';
import {
  IconPalette,
  IconFlask,
  IconBuildingFactory2,
  IconSettings,
} from '@tabler/icons-react';
import { PipelineSectionKey, RequestStatus } from '../../types/api';
import { STATUS_META } from '../../components/analytics/analytics-meta';

export interface SectionMeta {
  label: string;
  route: string;
  icon: React.ComponentType<{ size?: number; stroke?: number }>;
}

export const SECTION_META: Record<PipelineSectionKey, SectionMeta> = {
  bozzetti: { label: 'Bozzetti', route: '/bozzetti', icon: IconPalette },
  'pre-campionatura': { label: 'Pre-Campionatura', route: '/pre-campionatura', icon: IconFlask },
  campioni: { label: 'Campioni', route: '/campioni', icon: IconBuildingFactory2 },
  produzione: { label: 'Produzione', route: '/produzione', icon: IconSettings },
  gamma: { label: 'Gamma', route: '/gamma', icon: IconPalette },
};

// Lo stato PreCampionaturaCompleted (8) significa cose diverse per i due reparti: per
// Ufficio Stile è "ho finito, passata a Industrializzazione"; per Industrializzazione è
// "da prendere in carico". Le altre etichette arrivano da STATUS_META.
const STATUS_LABEL_OVERRIDES: Partial<Record<PipelineSectionKey, Partial<Record<RequestStatus, string>>>> = {
  'pre-campionatura': {
    [RequestStatus.PreCampionaturaCompleted]: 'Passate a Industrializzazione',
  },
  campioni: {
    [RequestStatus.PreCampionaturaCompleted]: 'Da prendere in carico',
  },
};

export function sectionStatusLabel(sectionKey: PipelineSectionKey, status: RequestStatus): string {
  return STATUS_LABEL_OVERRIDES[sectionKey]?.[status] ?? STATUS_META[status]?.label ?? `Stato ${status}`;
}

export type DeltaTone = 'up' | 'down' | 'flat' | 'new';

export interface DeltaInfo {
  text: string;
  tone: DeltaTone;
}

/**
 * Confronto tra il valore corrente e quello dello stesso periodo del mese scorso.
 * Con base 0 e valore attuale positivo non ha senso una percentuale: mostriamo "nuovo".
 */
export function formatDelta(current: number, previous: number): DeltaInfo {
  if (previous === 0 && current === 0) {
    return { text: '—', tone: 'flat' };
  }
  if (previous === 0) {
    return { text: 'nuovo', tone: 'new' };
  }

  const delta = current - previous;
  const pct = Math.round((delta / previous) * 100);
  const sign = delta > 0 ? '+' : '';
  const text = `${sign}${delta} (${pct > 0 ? '+' : ''}${pct}%)`;

  return { text, tone: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat' };
}

/** "scaduta da 3 gg" / "in scadenza oggi" / "tra 5 gg". Null/undefined se non c'è scadenza. */
export function formatDaysToDue(daysToDue?: number | null): string | null {
  if (daysToDue == null) return null;
  if (daysToDue < 0) return `scaduta da ${Math.abs(daysToDue)} g${Math.abs(daysToDue) === 1 ? '' : 'g'}`;
  if (daysToDue === 0) return 'in scadenza oggi';
  return `tra ${daysToDue} g${daysToDue === 1 ? '' : 'g'}`;
}

const MONTHS_IT_SHORT = [
  'gen', 'feb', 'mar', 'apr', 'mag', 'giu',
  'lug', 'ago', 'set', 'ott', 'nov', 'dic',
];

/** {year: 2026, month: 9} -> "set 26" */
export function formatMonthShort(year: number, month: number): string {
  const label = MONTHS_IT_SHORT[month - 1] ?? String(month);
  return `${label} ${String(year).slice(-2)}`;
}
